import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { api, CROP_TYPES, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import AddableSelect from "../components/AddableSelect";
import { Plus, Map, Trash2, Pencil, Sprout, CalendarClock, Users } from "lucide-react";
import { toast } from "sonner";

const blankPlot = { crop: "Corn", acres: "", plant_date: "", days_to_maturity: "", notes: "" };
const blank = { name: "", acreage: "", crop_type: "Corn", location: "", notes: "", plots: [{ ...blankPlot }], partner_id: "", partner_split_pct: 0, csr: "" };

function addDaysISO(plant, days) {
  if (!plant || !days) return "";
  const d = new Date(plant + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + parseInt(days, 10));
  return d.toISOString().slice(0, 10);
}

export default function Fields() {
  const { year, refreshYears } = useYear();
  const [fields, setFields] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [f, p] = await Promise.all([
      api.get(`/fields?year=${year}`),
      api.get(`/partners`),
    ]);
    setFields(f.data);
    setPartners(p.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const plotsTotalAcres = form.plots.reduce((s, p) => s + (parseFloat(p.acres) || 0), 0);
  const acreageNum = parseFloat(form.acreage) || 0;
  const plotsOverflow = plotsTotalAcres > acreageNum + 0.01;

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      acreage: acreageNum,
      crop_type: form.plots[0]?.crop || form.crop_type,
      location: form.location,
      notes: form.notes,
      year,
      partner_id: form.partner_id || "",
      partner_split_pct: parseFloat(form.partner_split_pct) || 0,
      csr: parseFloat(form.csr) || 0,
      plots: form.plots
        .filter((p) => (parseFloat(p.acres) || 0) > 0)
        .map((p) => ({
          id: p.id, // may be undefined for new plots — backend will assign
          crop: p.crop,
          acres: parseFloat(p.acres) || 0,
          plant_date: p.plant_date || "",
          days_to_maturity: parseInt(p.days_to_maturity, 10) || 0,
          notes: p.notes || "",
        })),
    };
    try {
      if (editing) {
        await api.put(`/fields/${editing.id}`, payload);
        toast.success("Field updated");
      } else {
        await api.post("/fields", payload);
        toast.success("Field added");
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      refreshYears();
      load();
    } catch {
      toast.error("Could not save field");
    }
  };

  const onEdit = (f) => {
    setEditing(f);
    setForm({
      name: f.name,
      acreage: f.acreage,
      crop_type: f.crop_type,
      location: f.location || "",
      notes: f.notes || "",
      partner_id: f.partner_id || "",
      partner_split_pct: f.partner_split_pct || 0,
      csr: f.csr || "",
      plots: (f.plots || []).length
        ? f.plots.map((p) => ({
            id: p.id,
            crop: p.crop || "",
            acres: p.acres ?? "",
            plant_date: p.plant_date || "",
            days_to_maturity: p.days_to_maturity || "",
            notes: p.notes || "",
          }))
        : [{ ...blankPlot, crop: f.crop_type, acres: f.acreage }],
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this field? Any yields attached to it stay but lose the field reference.")) return;
    await api.delete(`/fields/${id}`);
    toast.success("Field deleted");
    load();
  };

  const addPlot = () => setForm({ ...form, plots: [...form.plots, { ...blankPlot, crop: "Soybeans" }] });
  const updatePlot = (i, patch) => {
    const next = form.plots.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    setForm({ ...form, plots: next });
  };
  const removePlot = (i) => {
    if (form.plots.length === 1) return;
    setForm({ ...form, plots: form.plots.filter((_, idx) => idx !== i) });
  };

  return (
    <div>
      <TopBar
        title="Fields"
        subtitle={`Split each field by crop, set plant dates, and the harvest window appears on the calendar — ${year}.`}
        actions={
          <button
            data-testid="add-field-btn"
            onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
            className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2 mt-6"
          >
            <Plus className="w-4 h-4" /> New Field
          </button>
        }
      />

      {fields.length === 0 ? (
        <EmptyState icon={Map} title="No fields yet" hint={`Add your first field for ${year}, then split it by crop.`} testid="empty-fields" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {fields.map((f) => (
            <div key={f.id} className="bg-white border border-[#1F1E1B] p-5 card-hover" data-testid={`field-card-${f.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display text-xl font-bold">{f.name}</h3>
                  {f.location && <div className="text-xs text-[#66645E] mt-1">{f.location}</div>}
                  {f.partner_id && (() => {
                    const p = partners.find((x) => x.id === f.partner_id);
                    return p ? (
                      <div className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 bg-[#5C4A2B] text-white text-[10px] font-bold uppercase tracking-wider" data-testid={`field-partner-badge-${f.id}`}>
                        <Users className="w-3 h-3" /> {p.name} · {fmtNum(f.partner_split_pct, 1)}%
                      </div>
                    ) : null;
                  })()}
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black tabular">{fmtNum(f.acreage, 2)}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">acres</div>
                  {f.csr ? (
                    <div className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#66645E]" data-testid={`field-csr-${f.id}`}>
                      CSR <span className="tabular font-bold text-[#1A4135]">{fmtNum(f.csr, 1)}</span>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Plots */}
              <div className="border-t border-[#E5E2D6] pt-3 space-y-2" data-testid={`field-plots-${f.id}`}>
                {(f.plots || []).map((p, i) => (
                  <div key={p.id || i} className="flex items-baseline justify-between text-sm" data-testid={`field-plot-${f.id}-${i}`}>
                    <div className="flex items-center gap-2">
                      <Sprout className="w-3.5 h-3.5 text-[#1A4135]" />
                      <span className="stamp stamp-green">{p.crop}</span>
                      <span className="tabular text-[#66645E]">{fmtNum(p.acres, 1)} ac</span>
                    </div>
                    {p.expected_harvest_date && (
                      <span className="inline-flex items-center gap-1 text-[11px] tabular text-[#5C4A2B]" title={`Plant ${p.plant_date} + ${p.days_to_maturity}d`}>
                        <CalendarClock className="w-3 h-3" /> {p.expected_harvest_date}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {f.notes && <div className="text-sm text-[#66645E] mt-3 border-t border-[#E5E2D6] pt-3">{f.notes}</div>}

              <div className="flex gap-2 mt-4 pt-3 border-t border-[#E5E2D6]">
                <Link
                  data-testid={`view-field-${f.id}`}
                  to={`/fields/${f.id}`}
                  className="flex-1 bg-[#1A4135] text-white py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#133128] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Sprout className="w-3 h-3" /> Detail
                </Link>
                <button data-testid={`edit-field-${f.id}`} onClick={() => onEdit(f)} className="border border-[#1F1E1B] py-1.5 px-3 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center justify-center gap-1.5">
                  <Pencil className="w-3 h-3" />
                </button>
                <button data-testid={`delete-field-${f.id}`} onClick={() => onDelete(f.id)} className="border border-[#BC4727] text-[#BC4727] py-1.5 px-3 text-xs font-bold uppercase tracking-wider hover:bg-[#BC4727] hover:text-white transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Field" : "New Field"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="field-form">
            <Field label="Field Name" required>
              <input data-testid="field-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Total Acreage" required>
                <input data-testid="field-acreage-input" type="number" step="0.01" required value={form.acreage} onChange={(e) => setForm({ ...form, acreage: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Location / Description">
                <input data-testid="field-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. North 40" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>

            <Field label="CSR / CSR2 (Crop Suitability Rating, 0–100)">
              <input
                data-testid="field-csr-input"
                type="number" step="0.1" min="0" max="100"
                value={form.csr}
                onChange={(e) => setForm({ ...form, csr: e.target.value })}
                placeholder="e.g. 82"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
              />
            </Field>

            {/* Crop-share partner */}
            <div className="border-2 border-[#1F1E1B] bg-[#F5F4F0] p-3" data-testid="field-partner-section">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">Cost-Share Partner (optional)</div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  data-testid="field-partner-select"
                  value={form.partner_id}
                  onChange={(e) => {
                    const p = partners.find((x) => x.id === e.target.value);
                    setForm({ ...form, partner_id: e.target.value, partner_split_pct: p?.default_split_pct ?? form.partner_split_pct });
                  }}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                >
                  <option value="">— No partner —</option>
                  {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="relative">
                  <input
                    data-testid="field-split-input"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    placeholder="Partner's %"
                    value={form.partner_split_pct}
                    onChange={(e) => setForm({ ...form, partner_split_pct: e.target.value })}
                    disabled={!form.partner_id}
                    className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular disabled:bg-[#F5F4F0] disabled:text-[#9E9B90] pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#66645E]">%</span>
                </div>
              </div>
              {form.partner_id && (
                <div className="text-[11px] text-[#66645E] mt-1.5">
                  Expenses on this field can be split with this partner — bill them from Partners → Generate Invoice.
                </div>
              )}
            </div>

            {/* Plot splits */}
            <div className="border-2 border-[#1F1E1B] bg-[#FAF7EC] p-3" data-testid="field-plots-editor">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Crop Splits</div>
                  <div className="text-[11px] text-[#66645E]">
                    {fmtNum(plotsTotalAcres, 2)} / {fmtNum(acreageNum, 2)} ac allocated
                    {plotsOverflow && <span className="text-[#BC4727] font-bold ml-2">over by {fmtNum(plotsTotalAcres - acreageNum, 2)}</span>}
                  </div>
                </div>
                <button type="button" data-testid="add-plot-btn" onClick={addPlot} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Split
                </button>
              </div>

              <div className="space-y-3">
                {form.plots.map((p, i) => {
                  const eh = addDaysISO(p.plant_date, p.days_to_maturity);
                  return (
                    <div key={i} className="bg-white border border-[#1F1E1B] p-3 space-y-2" data-testid={`plot-row-${i}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Crop" required>
                          <AddableSelect
                            domain="crop"
                            defaults={CROP_TYPES}
                            value={p.crop}
                            onChange={(v) => updatePlot(i, { crop: v })}
                            testid={`plot-${i}-crop`}
                            required
                          />
                        </Field>
                        <Field label="Acres" required>
                          <input data-testid={`plot-${i}-acres`} required type="number" step="0.01" value={p.acres} onChange={(e) => updatePlot(i, { acres: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                        </Field>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Plant Date">
                          <input data-testid={`plot-${i}-plant-date`} type="date" value={p.plant_date} onChange={(e) => updatePlot(i, { plant_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                        </Field>
                        <Field label="Days to Maturity">
                          <input data-testid={`plot-${i}-dtm`} type="number" min="0" step="1" placeholder="e.g. 110" value={p.days_to_maturity} onChange={(e) => updatePlot(i, { days_to_maturity: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                        </Field>
                      </div>
                      {eh && (
                        <div className="bg-[#1F1E1B] text-white px-3 py-1.5 text-xs flex items-center justify-between" data-testid={`plot-${i}-harvest-preview`}>
                          <span className="font-bold uppercase tracking-wider">Expected Harvest</span>
                          <span className="tabular font-bold">{eh}</span>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button type="button" data-testid={`remove-plot-${i}`} disabled={form.plots.length === 1} onClick={() => removePlot(i)} className="text-[#BC4727] text-xs font-bold uppercase tracking-wider hover:underline disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Remove split
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Field label="Field Notes">
              <textarea data-testid="field-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} data-testid="field-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="field-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Field</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4 animate-in fade-in duration-150" data-testid="modal">
      <div className="bg-white w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-auto rounded-t-xl sm:rounded-xl shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#F0ECE0] px-5 py-3.5 bg-white/80 backdrop-blur sticky top-0 z-10">
          <h2 className="font-display text-lg font-bold text-[#1F2A47]">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="w-8 h-8 flex items-center justify-center rounded-full text-[#66645E] hover:bg-[#F5F4F0] hover:text-[#1F2A47] text-xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#66645E] mb-1.5">
        {label}{required && <span className="text-[#BC4727] ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}
